import { CaretDown, MagnifyingGlass, Plus, X } from "@phosphor-icons/react";
import { Flex, Popover, Text, TextField } from "@radix-ui/themes";
import cx from "classnames";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { HomeProjectChatInfoButton } from "./HomeProjectChatInfoButton";

import { useAskPunttProjects } from "../../../utils/queries/askPunttProjects";

export type HomeProjectPickerSelection = {
  id: string;
  name: string;
  sharedChatId: string | null;
};

/**
 * When the list has more than this many projects, the list scrolls and the
 * viewport shows {@link HOME_PROJECT_PICKER_VISIBLE_ROWS} rows. At or below
 * this count, every project is visible with no scroll.
 */
export const HOME_PROJECT_PICKER_SCROLL_THRESHOLD = 10;

/** Visible project rows in the scroll viewport when the list is long. */
export const HOME_PROJECT_PICKER_VISIBLE_ROWS = 9;

/** Approx row height (h-9) for the scroll max-height. */
const PROJECT_ROW_HEIGHT_PX = 36;

/** Client-side name/description match for the home picker search field. */
export function filterHomeProjectsBySearch<
  T extends { name: string; description?: string | null },
>(projects: T[], search: string): T[] {
  const needle = search.trim().toLowerCase();
  if (!needle) return projects;
  return projects.filter(
    (project) =>
      project.name.toLowerCase().includes(needle) ||
      (project.description ?? "").toLowerCase().includes(needle),
  );
}

/**
 * Membership list for the home picker: owned ∪ allowList, newest first.
 * Do not use `scope: "all"` here — MG admins get the whole enterprise.
 */
export function mergeHomeMemberProjects<
  T extends { _id: string; updatedAt?: string | Date },
>(owned: T[], shared: T[]): T[] {
  const byId = new Map<string, T>();
  for (const project of owned) {
    byId.set(project._id, project);
  }
  for (const project of shared) {
    if (!byId.has(project._id)) {
      byId.set(project._id, project);
    }
  }
  return Array.from(byId.values()).sort((a, b) =>
    String(b.updatedAt ?? "").localeCompare(String(a.updatedAt ?? "")),
  );
}

/**
 * Project selector for Ask Puntt home Project mode. Renders as a flush
 * extension under the composer: left/right/bottom border only (no top),
 * transparent, no shadow. Dropdown pins New Project at the base; only the
 * project list scrolls (capped to ~9 visible rows when >10 projects).
 *
 * Prefetches owned + shared-with-me while mounted (lane only mounts in
 * Project mode). Search filters the loaded list in memory — do not pass
 * `search` into `useAskPunttProjects` (query-key remount clears the list).
 */
export function HomeProjectPicker({
  selectedProjectId,
  selectedProjectName,
  onSelect,
  onClear,
  onNewProject,
  testIdPrefix = "ask-puntt-home",
  variant = "lane",
}: {
  selectedProjectId: string | null;
  selectedProjectName: string | null;
  onSelect: (project: HomeProjectPickerSelection) => void;
  onClear: () => void;
  onNewProject: () => void;
  /** Prefix every repeated child testid when a second picker is on screen. */
  testIdPrefix?: string;
  /** `lane` tucks under the home composer; `inline` is the walkthrough compose step. */
  variant?: "lane" | "inline";
}) {
  const { t } = useTranslation("ui");
  const { t: tCommon } = useTranslation("common");
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const ownedQuery = useAskPunttProjects({
    scope: "mine",
    enabled: true,
  });
  const sharedQuery = useAskPunttProjects({
    scope: "shared",
    enabled: true,
  });
  const allProjects = useMemo(
    () =>
      mergeHomeMemberProjects(
        ownedQuery.data?.projects ?? [],
        sharedQuery.data?.projects ?? [],
      ),
    [ownedQuery.data?.projects, sharedQuery.data?.projects],
  );
  const projects = filterHomeProjectsBySearch(allProjects, search);
  // Pending + empty → loading. Error (or settled empty) → empty copy so the
  // menu never sticks on "Loading…" if the list request fails.
  const isLoading =
    (ownedQuery.isPending || sharedQuery.isPending) && allProjects.length === 0;
  const hasSearch = search.trim().length > 0;
  const listScrolls = projects.length > HOME_PROJECT_PICKER_SCROLL_THRESHOLD;
  const listMaxHeightPx =
    HOME_PROJECT_PICKER_VISIBLE_ROWS * PROJECT_ROW_HEIGHT_PX;

  const hasSelectedProject = Boolean(selectedProjectName?.trim());
  const triggerLabel = hasSelectedProject
    ? selectedProjectName!
    : t("home.projectPickerPlaceholder");

  const isLane = variant === "lane";

  return (
    <div
      // Lane: extension under the composer (L/R/bottom border, no top). -mt-4
      // tucks under rounded-2xl; pt-5 keeps the visible gap above the dropdown
      // equal to bottom padding so the trigger is not cropped by the composer.
      // Inline: no tuck/border — used inside the Try compose step.
      className={
        isLane
          ? "-mt-4 flex w-full items-center gap-1 rounded-b-2xl border-x border-b border-puntt-neutral-gray-6 px-1 pb-1 pt-5"
          : "flex w-full items-center gap-1"
      }
      data-testid={`${testIdPrefix}-project-section`}
    >
      <Popover.Root
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setSearch("");
        }}
      >
        <Popover.Trigger>
          <button
            type="button"
            data-testid={`${testIdPrefix}-project-picker`}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-label={t("home.projectPickerAriaLabel", {
              project: triggerLabel,
            })}
            className={cx(
              "flex h-8 max-w-full items-center gap-1.5 rounded-xl border border-puntt-neutral-gray-6 bg-transparent px-3 text-sm font-medium transition-colors",
              "hover:bg-puntt-neutral-gray-2",
              // Empty state matches the chat textarea placeholder (gray-9).
              hasSelectedProject
                ? "text-puntt-neutral-gray-12"
                : "text-puntt-neutral-gray-9",
            )}
          >
            <Text size="2" weight="medium" className="truncate">
              {triggerLabel}
            </Text>
            <CaretDown
              size={12}
              weight="bold"
              aria-hidden="true"
              className={cx(
                "shrink-0 text-puntt-neutral-gray-9 transition-transform duration-150",
                open && "rotate-180",
              )}
            />
          </button>
        </Popover.Trigger>
        <Popover.Content
          align="start"
          sideOffset={6}
          // No overflow on the shell — only the project list scrolls so New
          // Project stays pinned to the base.
          className="flex w-[280px] flex-col overflow-hidden p-0"
          data-testid={`${testIdPrefix}-project-menu`}
        >
          <div className="p-2 pb-1">
            <TextField.Root
              data-testid={`${testIdPrefix}-project-search`}
              placeholder={t("searchProjects")}
              size="2"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label={t("searchProjects")}
              className="w-full"
            >
              <TextField.Slot>
                <MagnifyingGlass size={14} aria-hidden="true" />
              </TextField.Slot>
            </TextField.Root>
          </div>

          <div
            role="listbox"
            aria-label={t("projects")}
            data-testid={`${testIdPrefix}-project-list`}
            className={cx(
              "flex flex-col gap-0.5 px-2",
              listScrolls && "overflow-y-auto",
            )}
            style={listScrolls ? { maxHeight: listMaxHeightPx } : undefined}
          >
            {isLoading ? (
              <Text
                size="2"
                data-testid={`${testIdPrefix}-project-loading`}
                className="p-2 text-puntt-neutral-gray-9"
              >
                {t("home.projectPickerLoading")}
              </Text>
            ) : projects.length === 0 ? (
              <Text
                size="2"
                data-testid={`${testIdPrefix}-project-empty`}
                className="p-2 text-puntt-neutral-gray-9"
              >
                {hasSearch
                  ? t("home.projectPickerNoResults", { query: search.trim() })
                  : t("home.projectPickerEmpty")}
              </Text>
            ) : (
              projects.map((project) => {
                const selected = project._id === selectedProjectId;
                return (
                  <button
                    key={project._id}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    data-testid={`${testIdPrefix}-project-option-${project._id}`}
                    className={cx(
                      "flex h-9 w-full shrink-0 cursor-pointer items-center rounded-lg bg-transparent px-2 text-left text-sm transition-colors",
                      selected
                        ? "bg-puntt-neutral-gray-3 text-puntt-neutral-gray-12"
                        : "text-puntt-neutral-gray-12 hover:bg-puntt-neutral-gray-2",
                    )}
                    onClick={() => {
                      onSelect({
                        id: project._id,
                        name: project.name,
                        sharedChatId: project.sharedChatId ?? null,
                      });
                      setOpen(false);
                      setSearch("");
                    }}
                  >
                    <Text size="2" className="truncate">
                      {project.name}
                    </Text>
                  </button>
                );
              })
            )}
          </div>

          <div className="mt-1 border-t border-puntt-neutral-gray-5 p-2 pt-1.5">
            <button
              type="button"
              data-testid={`${testIdPrefix}-new-project`}
              className="flex h-9 w-full cursor-pointer items-center gap-2 rounded-lg bg-transparent px-2 text-left text-sm font-medium text-puntt-neutral-gray-12 transition-colors hover:bg-puntt-neutral-gray-2"
              onClick={() => {
                setOpen(false);
                setSearch("");
                onNewProject();
              }}
            >
              <Plus size={14} weight="bold" aria-hidden="true" />
              <Flex align="center">{tCommon("nav.newProject")}</Flex>
            </button>
          </div>
        </Popover.Content>
      </Popover.Root>
      {hasSelectedProject ? (
        <button
          type="button"
          data-testid={`${testIdPrefix}-project-clear`}
          aria-label={t("home.clearProjectSelectionAriaLabel")}
          className="flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-xl border border-puntt-neutral-gray-6 bg-transparent text-puntt-neutral-gray-9 transition-colors hover:bg-puntt-neutral-gray-2 hover:text-puntt-neutral-gray-12"
          onClick={() => {
            setOpen(false);
            setSearch("");
            onClear();
          }}
        >
          <X size={12} weight="bold" aria-hidden="true" />
        </button>
      ) : null}
      {isLane ? (
        <HomeProjectChatInfoButton testIdPrefix={testIdPrefix} />
      ) : null}
    </div>
  );
}
